import { Button, Typography } from '@sureco/design-system'
import { Alert, Modal, Select } from 'antd'
import useFeatureFlags from 'hooks/useFeatureFlags'
import React, { memo, useEffect, useRef } from 'react'
import ClientSelect from '../../../../../../components/ClientSelect'
import useUploadCensusFile from '../hooks/useUploadCensusFile'

const { Option } = Select

const AssistedManualCensusUploadModal = ({
  open,
  onOk,
  onCancel,
}: {
  open: boolean
  onOk: (isQueued: boolean) => void
  onCancel: () => void
}) => {
  const { flags } = useFeatureFlags()
  const isSplitEntityEnabled = flags['admin.ama-census-split-entity-enabled']
  const {
    transformedClient,
    selectedFiles,
    selectedFile,
    fileInputRef,
    selectedClient,
    selectedCensusVersion,
    handleUpload,
    handleCompanyChange,
    handleCensusVersionChange,
    handleFileSelect,
    loading,
    openFileDialog,
    onCloseModal,
  } = useUploadCensusFile({
    onOk,
    onCancel,
    isSplitEntityEnabled,
  })

  const confirmRef = useRef<ReturnType<typeof Modal.confirm> | null>(null)

  useEffect(() => {
    if (loading) return
    if (!confirmRef.current) return

    confirmRef.current.destroy()
    confirmRef.current = null
  }, [loading])

  useEffect(() => {
    return () => {
      if (confirmRef.current) {
        confirmRef.current.destroy()
        confirmRef.current = null
      }
    }
  }, [])

  const handleCancel = () => {
    if (!loading) {
      onCloseModal()
      return
    }

    if (confirmRef.current) return

    confirmRef.current = Modal.confirm({
      title: 'Are you sure?',
      onOk: () => {
        confirmRef.current?.destroy()
        confirmRef.current = null
        onCloseModal()
      },
      onCancel: () => {
        confirmRef.current?.destroy()
        confirmRef.current = null
      },
    })
  }

  const isSplitClient = selectedClient?.isSplitClient ?? false
  const isSplitUploadUnsupported = isSplitClient && !isSplitEntityEnabled
  const isFileSelectionDisabled = loading || !selectedClient
  const uploadHelperText = isSplitClient
    ? 'You can upload up to 5 Census files for split clients.'
    : 'You can upload 1 Census file.'
  const selectedFileLabel =
    selectedFiles?.length > 1
      ? `Selected Files: ${selectedFiles.map(({ name }) => name).join(', ')}`
      : selectedFile
        ? `Selected File: ${selectedFile.name}`
        : null

  return (
    <Modal
      title="Upload HRIS Census Report"
      open={open}
      onOk={handleUpload}
      onCancel={handleCancel}
      okText={`Upload HRIS File for ${selectedClient?.name ?? '...'}`}
      maskClosable={!loading}
      okButtonProps={{
        disabled: !selectedClient || isSplitUploadUnsupported,
        loading,
      }}
    >
      <div>
        <Typography size={14}>Company:</Typography>
        <ClientSelect
          companies={transformedClient}
          defaultCompanyId={selectedClient?.id}
          onChange={handleCompanyChange}
          disabled={loading}
        />
      </div>
      <div style={{ marginTop: '1em' }}>
        <Typography size={14}>Census Version:</Typography>
        <Select
          value={selectedCensusVersion}
          onChange={handleCensusVersionChange}
          style={{ width: '100%' }}
          placeholder="Select census version"
          disabled={loading}
        >
          <Option value={1}>Version 1</Option>
          <Option value={2}>Version 2</Option>
        </Select>
      </div>
      <input
        type="file"
        accept=".csv"
        multiple={isSplitClient}
        ref={fileInputRef}
        style={{ display: 'none' }}
        onChange={handleFileSelect}
      />
      {isSplitUploadUnsupported ? (
        <Alert
          style={{ marginTop: '1em' }}
          type="warning"
          showIcon
          message="This feature is not supported."
        />
      ) : (
        <>
          <Button
            style={{ marginTop: '1em' }}
            onClick={openFileDialog}
            disabled={isFileSelectionDisabled}
          >
            {isSplitClient ? 'Select Multiple Files' : 'Select File'}
          </Button>
          {selectedClient ? (
            <Alert
              style={{ marginTop: '1em' }}
              type="info"
              showIcon
              message={uploadHelperText}
            />
          ) : null}
        </>
      )}
      {selectedFileLabel ? <p>{selectedFileLabel}</p> : null}
    </Modal>
  )
}

export default memo(AssistedManualCensusUploadModal)
